import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { IPlanDetails } from '@data/plan/plan-details.interface';
import { SearchCriteria } from '@data/quick-search/quick-search.interface';
import { IExternalSSO, IExternalSSOParams } from '@data/quotes/external-sso.interface';
import { FinalQuotes, MedicareSupplementQuotes, Pharmacy, maPdpQuotes } from '@data/quotes/quotes.interface';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { IExternalProvider, IExternalProviderType } from './external-provider.interface';
@Injectable({
  providedIn: 'root'
})
export class QuotesService {
  private alertSubject = new Subject<any>();
  private expandPanelSubject = new Subject<boolean>();
  private formSubject = new BehaviorSubject<any>("");
  private _mailOrderFlag = new Subject<any>();

  public medicareSupplement!: MedicareSupplementQuotes;
  public mapdFilterSetting:{
    carrierName: string[],
    rating: any,
    enrollment_methods: string[],
    benefit: string[],
  };
  public mapdFilterSettingWithLead:{
    carrierName: string[],
    rating: any,
    enrollment_methods: string[],
    benefit: string[],
  };
  public medsupFilterSetting:{
    carrierName: string[],
    enrollment_methods: string[]
  };
  public medsupFilterSettingWithLead:{
    carrierName: string[],
    enrollment_methods: string[]
  }
  public tabName: string = "MA & PDP";
  public finalExp: FinalQuotes;
  public selectedPharmacy: Pharmacy[];
  public mapdData!: any;
  public providers: any;
  private _externalProviderTypes: IExternalProviderType[] = [];
  private _externalProviderTypes$?: Observable<IExternalProviderType[]>;

  constructor(
    private http: HttpClient,
  ) { }


 public setMailOrderFlag(newVal:any) {
    this._mailOrderFlag.next(newVal);
  }

  public getMailOrderFlag() {
    return this._mailOrderFlag.asObservable();
  }

  public sendCloseAlert(isClose: boolean) {
    this.alertSubject.next(isClose);
  }

  public getCloseAlert(): Observable<any> {
    return this.alertSubject.asObservable();
  }

  public sendFormAlert(value: string) {
    this.formSubject.next(value);
  }

  public getFormAlert(): Observable<any> {
    return this.formSubject.asObservable();
  }


  public sendExpandPanelAlert(value: boolean) {
    this.expandPanelSubject.next(value);
  }

  public getExpandPanelAlert(): Observable<any> {
    return this.expandPanelSubject.asObservable();
  }


  public getUserCarrier(payload: SearchCriteria) {
    return this.http.post(environment.api + 'user/usercarrier/search/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getNPN(payload: object) {
    return this.http.post(environment.api + 'user/checkUserEnrollmentEligible', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
  public getExternalSSO(payload: IExternalSSOParams) {
    return this.http.post<IExternalSSO>(environment.api + 'externalSSO/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public saveFinalQuotes(payload: FinalQuotes) {
    return this.http.post(environment.api + 'quote', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getFinalQuotes(payload: FinalQuotes) {
    return this.http.post(environment.api + 'quote/QuoteRequest/fex', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public reportQuotes(payload: any) {
    return this.http.post(environment.api + 'emailMessage/send', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getDocument(payload: SearchCriteria) {
    return this.http.post(environment.api + 'document/search', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }


  public getDrugNames(params: any) {
    return this.http.post<any>(environment.api + `drug/getDrugNames`, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public searchByDrugName(params: any) {
    return this.http.post(environment.api + `drug/searchByDrugName`, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public searchByZip(params: any) {
    return this.http.post(environment.api + `pharmacy/searchByZip`, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public searchOnlinePharmacy(params: any) {
    return this.http.post(environment.api + `pharmacyOnline/search`, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getCountiesByZip(zipCode: any) {
    return this.http.get(environment.api + `geo/countiesByZip/` + zipCode).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public searchProvider(data: any) {
    return this.http.post<IExternalProvider[]>(environment.api + `externalProvider/search`, data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getExternalProviderTypes(): Observable<IExternalProviderType[]> {
    if (!this._externalProviderTypes$) {
      this._externalProviderTypes$ = this.http.get<IExternalProviderType[]>(environment.api + 'externalProvider/types/').pipe(
        tap((providerTypes: IExternalProviderType[]) => {
          this._externalProviderTypes = providerTypes || [];
        }),
        shareReplay(1),
        catchError((error) => {
          this._externalProviderTypes$ = undefined;
          return throwError(() => error);
        })
      );
    }

    return this._externalProviderTypes$!;
  }

  public getExternalProviderTypeName(providerTypeId: string | number | undefined | null, emptyDisplay: string = 'N/A'): string {
    let providerType = this._externalProviderTypes.find((type: IExternalProviderType) =>
      type.external_provider_type_id == providerTypeId?.toString()
    );

    return providerType?.external_provider_type || providerTypeId?.toString() || emptyDisplay;
  }

  public refreshExternalProviderTypes(): Observable<IExternalProviderType[]> {
    this._externalProviderTypes$ = undefined;
    return this.getExternalProviderTypes().pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getProductType(product_type_name: string) {
    let payload = {
      "search_type": "simple",
      "criteria": { "product_type_name": { "type": "simple", "value": [product_type_name] } },
      "field_list": "",
      "page_number": 1,
      "records_per_page": 10
    }
    return this.http.post(environment.api + 'productType/search/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getDrugDetail(drug_id: string) {
    return this.http.get(environment.api + 'drug/getDrugDetail/' + drug_id).pipe(
      catchError((error) => throwError(() => error)),
    );
  }

  public getDrugDosageDetail(payload: object) {
    return this.http.post(environment.api + 'drug/getDrugDosageDetail', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public updateMedication(leadId: string, drugId: string, payload: object) {
    return this.http.post(environment.api + 'lead/' + leadId + '/drug/' + drugId, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getMedicareQuotes(payload: any) {
    return this.http.post(environment.api + 'quote/QuoteRequest/medsup', payload).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public emailQuote(client_invite_id: string,payload:any) {
    return this.http.post((environment.api) + `clientInvite/` + client_invite_id, payload).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public isTobaccoQuestionRequired(zip: string) {
    return this.http.get((environment.api) + `quote/isTobaccoQuestionRequired/` + zip).pipe(catchError(error => {
      return throwError(() => error);
    })
    );
  }

  public deleteEmailQuote(client_invite_id: string) {
    return this.http.get((environment.api) + `clientInvite/` + client_invite_id + `/delete`).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public getPlanDetail(payload: any) {
    return this.http.post<IPlanDetails>(environment.api + 'quote/planDetail/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getInNetworkPlan(payload: object) {
    return this.http.post(environment.api + 'pharmacy/searchPlanNetwork', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getPharmacyDetail(pharmacyId: any) {
    return this.http.get(environment.api + `pharmacy/` + pharmacyId + `/detail`).pipe(catchError((error) => {
      return throwError(() => error);
    })
    )
  }

  public getQuotes(params: maPdpQuotes) {
    return this.http.post(environment.api + `quote/QuoteRequest/mapd`, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public userCarrierSearch(payload: SearchCriteria) {
    return this.http.post(environment.api + 'usercarrier/search/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public leadEmailSearch(payload: SearchCriteria, leadId: string) {
    return this.http.post(environment.api + 'lead/' + leadId + '/email/search', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public addLeadDrug(leadId: string, payload: object) {
    return this.http.post(environment.api + 'lead/' + leadId + '/drug', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public addLeadExternalProvider(leadId: string, payload: object) {
    return this.http.post(environment.api + 'lead/' + leadId + '/externalProvider', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public addMailOrderPharmacy(leadId: string, payload: object) {
    return this.http.post(environment.api + 'lead/' + leadId + '/pharmacy', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public searchDrugs(payload: SearchCriteria, leadId: string) {
    return this.http.post(environment.api + 'lead/' + leadId + '/drug/search', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public searchPharmacy(payload: SearchCriteria, leadId: string) {
    return this.http.post(environment.api + 'lead/' + leadId + '/pharmacy/search', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public providerSearch(payload: SearchCriteria, leadId: string) {
    return this.http.post(environment.api + 'lead/' + leadId + '/externalProvider/search', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public addInNetworkPharmacy(leadId: string, pharmacyId: string, payload: object) {
    return this.http.post(environment.api + 'lead/' + leadId + '/pharmacy/' + pharmacyId, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public deleteNetworkPharmacy(leadId: string, pharmacyId: string) {
    return this.http.get(environment.api + 'lead/' + leadId + '/pharmacy/' + pharmacyId + '/delete').pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public toggleProvider(leadId: string, provider_id: string, payload: object) {
    return this.http.post(environment.api + 'lead/' + leadId + '/externalProvider/' + provider_id, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public deleteProvider(leadId: string, provider_id: string,) {
    return this.http.get(environment.api + 'lead/' + leadId + '/externalProvider/' + provider_id + '/delete').pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public getHealthProvider(payload: any) {
    return this.http.post<IExternalProvider[]>(environment.api + 'externalProvider/searchByProvider/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public pdfABE(payload : any) {
    return this.http.post((environment.insfire) + `api/ABE`, payload).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public checkPhysicianLetterPosting(vccPayload: any) {
  if (!vccPayload) return;
    return this.addPhysicianVerificationMailLead(vccPayload).subscribe({
      error: (err) => { },
    });
}

  public addPhysicianVerificationMailLead(payload : object) {
    return this.http.post(environment.api + 'physicianVerificationMailer/', payload).pipe(
      catchError((error) => throwError(() => error)),
    );
  }

  public getCarrierQuoteSource(params: any) {
    return this.http.post((environment.api) + `carrierQuoteSource/search/`, params).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }
}
